import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';
import { topicList } from '@/mocks/topics';
import { newsList } from '@/mocks/news';
import { formatHeat, statusStyle } from '@/utils/format';

function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const topic = topicList.find((t) => t.slug === slug);

  if (!topic) {
    return (
      <SiteLayout>
        <Seo
          title="专题不存在 - 网报吃瓜"
          description="你访问的专题不存在或已下线，可返回专题库浏览其他正在跟踪的热点事件。"
          keywords="网报吃瓜专题,网报吃瓜"
          canonicalPath="/topics"
        />
        <div className="w-full px-4 pb-20 pt-32 md:px-8">
          <div className="mx-auto max-w-xl rounded-lg border border-background-200/80 bg-background-100 py-16 text-center">
            <i className="ri-file-unknow-line text-4xl text-foreground-400" />
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
              这个专题暂时找不到了
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              它可能已经下线，或者链接输错了。去专题库看看正在跟踪的热点吧。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-route-line" />
              返回专题库
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const relatedNews = newsList.filter((n) => n.topicSlug === topic.slug);
  const relatedTopics = topicList.filter((t) => t.slug !== topic.slug).slice(0, 3);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: topic.title,
      articleSection: topic.category,
      description: topic.summary,
      datePublished: topic.startedAt,
      inLanguage: 'zh-CN',
      articleBody: topic.timeline.map((t) => `${t.time} ${t.title}：${t.desc}`).join(' '),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '专题库', item: `${window.location.origin}/topics` },
        {
          '@type': 'ListItem',
          position: 3,
          name: topic.title,
          item: `${window.location.origin}/topic/${topic.slug}`,
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${topic.title} - 网报吃瓜专题跟踪`}
        description={`${topic.summary} 网报吃瓜持续跟踪该事件的时间线与媒体报道差异。`}
        keywords={`网报吃瓜,${topic.category}热点,网报吃瓜报道,网报吃瓜转载,${topic.title}`}
        canonicalPath={`/topic/${topic.slug}`}
        schema={schema}
      />

      <div className="w-full px-4 pt-24 md:px-8 md:pt-32">
        <Breadcrumbs
          items={[
            { label: '首页', to: '/' },
            { label: '专题库', to: '/topics' },
            { label: topic.title },
          ]}
        />

        <header className="mt-6 overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
          <div className="relative h-[240px] w-full md:h-[340px]">
            <img
              src={topic.cover}
              alt={`${topic.title} - 网报吃瓜专题封面`}
              title={`${topic.title} - 网报吃瓜专题跟踪`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-background-50/95 px-2.5 py-1 text-xs font-medium text-foreground-900">
                  {topic.category}
                </span>
                <span
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                    statusStyle[topic.status] || 'bg-accent-500 text-background-50'
                  }`}
                >
                  {topic.status}
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-50">
                  <i className="ri-fire-fill text-primary-300" />
                  热度 {formatHeat(topic.heat)}
                </span>
              </div>
              <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                {topic.title}
              </h1>
            </div>
          </div>
          <div className="p-5 md:p-8">
            <p className="max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {topic.summary}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-background-200/80 pt-5 md:grid-cols-4">
              <div>
                <div className="text-xs text-foreground-500">起于</div>
                <div className="mt-1 font-heading text-sm font-bold text-foreground-950">
                  {topic.startedAt}
                </div>
              </div>
              <div>
                <div className="text-xs text-foreground-500">累计更新</div>
                <div className="mt-1 font-heading text-sm font-bold text-foreground-950">
                  {topic.updateCount} 条
                </div>
              </div>
              <div>
                <div className="text-xs text-foreground-500">关注人数</div>
                <div className="mt-1 font-heading text-sm font-bold text-foreground-950">
                  {topic.followers}
                </div>
              </div>
              <div>
                <div className="text-xs text-foreground-500">当前状态</div>
                <div className="mt-1 font-heading text-sm font-bold text-foreground-950">
                  {topic.status}
                </div>
              </div>
            </div>
          </div>
        </header>
      </div>

      <div className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-12">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              事件时间线
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              按发生顺序整理，每条标注信源与所处阶段，便于判断事件推进到哪一步。
            </p>

            <ol className="mt-8">
              {topic.timeline.map((item, index) => (
                <li key={item.title} className="relative flex gap-4 md:gap-6">
                  <div className="relative flex shrink-0 flex-col items-center">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-background-50">
                      {index + 1}
                    </span>
                    {index !== topic.timeline.length - 1 && (
                      <span className="mt-1 w-px flex-1 bg-background-300" />
                    )}
                  </div>
                  <div className={index === topic.timeline.length - 1 ? 'pb-2' : 'pb-8'}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-foreground-500">{item.time}</span>
                      <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                        {item.tag}
                      </span>
                    </div>
                    <h3 className="mt-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-link" />
                      信源：{item.source}
                    </span>
                  </div>
                </li>
              ))}
            </ol>

            <section className="mt-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                媒体报道对比
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                同一节点下不同媒体的标题与切入角度并列，方便看出报道口径的差异。
              </p>
              <div className="mt-6 space-y-4">
                {topic.reports.map((report) => (
                  <article
                    key={report.title}
                    className="flex flex-col gap-3 rounded-lg border border-background-200/80 bg-background-100 p-5 md:flex-row md:items-start md:gap-5"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-500 text-xl text-background-50">
                      <i className="ri-newspaper-line" />
                    </span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-900">
                          {report.media}
                        </span>
                        <span className="text-xs text-foreground-500">{report.time}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
                        {report.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                        切入角度：{report.angle}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                该专题相关快讯
              </h2>
              {relatedNews.length > 0 ? (
                <ul className="mt-4 space-y-3">
                  {relatedNews.map((item) => (
                    <li key={item.id} className="border-b border-background-200/80 pb-3 last:border-none last:pb-0">
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span>{item.date}</span>
                        <span>{item.time}</span>
                        <span className="text-accent-700">
                          <i className="ri-fire-fill" /> {formatHeat(item.heat)}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm leading-snug text-foreground-800">
                        {item.title}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-foreground-600">该专题暂无独立快讯收录。</p>
              )}
            </div>

            <div className="rounded-lg bg-secondary-950 p-5 text-background-100">
              <h2 className="font-heading text-base font-bold text-background-50">
                发现时间线有遗漏？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200/80">
                如果你掌握更早的信源或者补充进展，欢迎通过关于我们页面提交线索，核实后我们会补进时间线并标注修订记录。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-send-plane-line" />
                提交线索
              </Link>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              <ul className="mt-4 space-y-3">
                {relatedTopics.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/topic/${item.slug}`}
                      className="group flex items-start gap-3"
                    >
                      <span className="h-14 w-16 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.cover}
                          alt={`${item.title} - 网报吃瓜相关专题`}
                          title={`${item.title} - 网报吃瓜`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex-1">
                        <span className="block text-xs text-accent-700">{item.category}</span>
                        <span className="mt-1 block text-sm leading-snug text-foreground-800 group-hover:text-primary-700">
                          {item.title}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              更多正在跟踪的专题
            </h2>
            <Link
              to="/topics"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-700 hover:text-primary-800"
            >
              查看全部
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedTopics.map((item) => (
              <TopicCard key={item.slug} topic={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

export default TopicDetail;